import { useState, useEffect, useRef, useMemo, type ChangeEvent, type KeyboardEvent } from 'react';
import { Input } from "@akuna/ui";
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { Search } from 'lucide-react';
import { useItems } from '@/hooks/useItems';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { BarcodeScanner } from '@/components/ui/barcode-scanner';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

export interface PdvItem {
  id: string;
  name: string;
  sale_price: number;
  type?: "PRODUTO" | "SERVICO" | "PACOTE" | null;
  barcode?: string | null;
}

interface ProductSearchProps {
  onSelectProduct: (product: PdvItem) => void | Promise<void>;
  /** Keep results inside an enclosing modal so Radix does not block their pointer events. */
  popoverPortalled?: boolean;
}

export const ProductSearch = ({ onSelectProduct, popoverPortalled = true }: ProductSearchProps) => {
  const { t } = useTranslation("erp");
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [lastSelectedId, setLastSelectedId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const suppressAutoSelectRef = useRef(false);
  const lookupInFlightRef = useRef(false);
  const lastLookupRef = useRef<string | null>(null);
  const debouncedSearch = useDebouncedValue(search, 300);
  const { data: items, isLoading } = useItems({
    search: debouncedSearch,
    isActive: true,
    page: 1,
    pageSize: 50,
  });

  const itemsList = useMemo(
    () => ((Array.isArray(items) ? items : (items as { data?: unknown[] })?.data ?? []) as PdvItem[]),
    [items]
  );

  useEffect(() => {
    // Auto-select if searching by barcode and found exact match.
    if (
      !suppressAutoSelectRef.current &&
      search &&
      itemsList.length === 1 &&
      (itemsList[0] as PdvItem).barcode === search
    ) {
      void onSelectProduct(itemsList[0] as PdvItem);
      setSearch('');
    }
  }, [search, itemsList, onSelectProduct]);

  const sanitizeBarcode = (value: string) => value.replace(/\t/g, '').trim();

  const lookupBarcode = async (value?: string): Promise<boolean> => {
    const barcode = sanitizeBarcode(value ?? search);
    if (!barcode) return false;
    if (lookupInFlightRef.current && barcode === lastLookupRef.current) return false;

    lookupInFlightRef.current = true;
    suppressAutoSelectRef.current = true;
    lastLookupRef.current = barcode;

    const { data, error } = await supabase
      .from('items')
      .select('*')
      .eq('barcode', barcode)
      .eq('type', 'PRODUTO')
      .eq('is_active', true)
      .is('deleted_at', null)
      .limit(1)
      .maybeSingle();

    try {
      if (error) {
        toast.error(t('productSearch.lookupError'));
        return false;
      }

      if (!data) {
        toast.warning(t('productSearch.notFound'));
        return false;
      }

      void onSelectProduct(data);
      setSearch('');
      return true;
    } finally {
      suppressAutoSelectRef.current = false;
      lookupInFlightRef.current = false;
    }
  };

  const handleKeyDown = async (event: KeyboardEvent<HTMLInputElement>) => {
    // Prefer autocomplete selection via Command; only force barcode lookup for numeric barcodes.
    setOpen(true);
    const trimmed = search.trim();
    const looksLikeBarcode = /^\d{6,}$/.test(trimmed);

    if (event.key === 'Enter' && looksLikeBarcode) {
      event.preventDefault();
      await lookupBarcode();
    }
    if (event.key === 'Tab' && looksLikeBarcode) {
      // Many USB barcode readers append TAB; keep focus here and resolve the barcode.
      event.preventDefault();
      await lookupBarcode();
    }
    if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;

    if (nextValue.includes('\t')) {
      const sanitized = sanitizeBarcode(nextValue);
      setSearch(sanitized);
      void lookupBarcode(sanitized);
      return;
    }

    setSearch(nextValue);
  };

  const handleScan = (barcode: string) => {
    const sanitized = sanitizeBarcode(barcode);
    setSearch(sanitized);
    setOpen(false);
    return lookupBarcode(sanitized);
  };

  return (
    <div className="space-y-2 flex-1">
      <div className="relative flex gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverAnchor asChild>
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('productSearch.placeholder')}
                value={search}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onClick={() => setOpen(true)}
                style={{ paddingLeft: "2.5rem" }}
                ref={inputRef}
                role="combobox"
                aria-expanded={open}
                aria-autocomplete="list"
              />
            </div>
          </PopoverAnchor>
          <PopoverContent
            portalled={popoverPortalled}
            className="p-0"
            align="start"
            style={{ width: 'var(--radix-popover-trigger-width)' }}
            onOpenAutoFocus={(e) => {
              // Prevent popover from stealing focus from input
              e.preventDefault();
            }}
            onCloseAutoFocus={(e) => {
              // Avoid focus jumping away from the input when closing.
              e.preventDefault();
            }}
          >
            <Command shouldFilter={false}>
              <CommandList className="max-h-[300px] overflow-y-auto">
                {isLoading ? (
                  <div className="p-4 text-center text-muted-foreground">{t('productSearch.loading')}</div>
                ) : (
                  <>
                    <CommandEmpty>{t('productSearch.empty')}</CommandEmpty>
                    <CommandGroup>
                      {itemsList.map((item: PdvItem) => (
                        <CommandItem
                          key={item.id}
                          value={item.id}
                          onMouseDown={(e) => {
                            // Keep input focused so typing/backspace doesn't "freeze" after interacting with the list
                            e.preventDefault();
                          }}
                          onSelect={() => {
                            void onSelectProduct(item);
                            setLastSelectedId(item.id);
                            setSearch('');
                            setOpen(false);
                          }}
                          className={cn(
                            "items-start py-2 data-[selected=true]:bg-secondary data-[selected=true]:text-secondary-foreground",
                            lastSelectedId === item.id && "bg-secondary text-secondary-foreground",
                          )}
                        >
                          <div className="flex w-full flex-col gap-1">
                            <div className="font-medium">{item.name}</div>
                            <div className="text-sm text-muted-foreground flex justify-between">
                              <span>
                                {item.type === 'PRODUTO'
                                  ? t('common.product')
                                  : item.type === 'SERVICO'
                                    ? t('common.service')
                                    : t('common.package')}
                                {item.barcode ? ` • ${item.barcode}` : ''}
                              </span>
                              <span className="text-primary font-semibold">
                                R$ {item.sale_price?.toFixed(2) || '0.00'}
                              </span>
                            </div>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        <BarcodeScanner onScan={handleScan} buttonSize="default" />
      </div>
    </div>
  );
};
